import { useWishlist, wishlist } from "@/lib/wishlist/hooks";
import type { Cart } from "../useSelection";
import useLegacySelection from "../useSelection";
import useRemoveFromCart from "./removeFromCart";
import useUpdateQuantity from "./updateQuantity";
import { useChangeSize } from "./useChangeSize";
export type CartItem = Cart["items"][number];

const formatItem = (
  item: CartItem,
  remove: (item: CartItem) => void,
  update: (item: CartItem, newQuantity: number) => void,
  increment: (item: CartItem) => void,
  decrement: (item: CartItem) => void,
  moveToWishlist: (item: CartItem, onSuccess?: () => void) => void,
  changeSize: (item: CartItem, newSizeItemId: string) => void
) => {
  return {
    ...item,

    remove: () => remove(item),
    update: (newQuantity: number) => update(item, newQuantity),
    increment: () => {
      increment(item);
    },
    decrement: () => {
      decrement(item);
    },
    moveToWishlist: (onSuccess?: () => void) => {
      moveToWishlist(item, onSuccess);
    },
    changeSize: (newSizeItemId: string) => changeSize(item, newSizeItemId),
    inCart: (item.quantity || 0) > 0,
  };
};

export const useFormatItems = (items: CartItem[]) => {
  const { removeItem } = useRemoveFromCart();
  const { updateQuantity } = useUpdateQuantity();
  const { add } = useWishlist();
  const { changeSize: changeSizeHook } = useChangeSize();

  const remove = (item: CartItem) =>
    removeItem.mutate({
      line: item.id,
    });

  const update = (item: CartItem, newQuantity: number) => {
    updateQuantity.mutate({
      line: item.id,
      newQuantity,
    });
  };

  const increment = (item: CartItem) => {
    update(item, (item.quantity || 0) + 1);
  };

  const decrement = (item: CartItem) => {
    update(item, (item.quantity || 0) - 1);
  };

  const moveToWishlist = (item: CartItem, onSuccess?: () => void) => {
    remove(item);
    item.displayItemId &&
      add(
        {
          itemId: wishlist.toItemId(item.displayItemId),
          sizeItemId: item.itemId,
        },
        {
          onSuccess,
        }
      );
  };

  const changeSize = (item: CartItem, newSizeItemId: string) => {
    changeSizeHook.mutate({
      item,
      newSizeItemId,
    });
  };

  const formattedItems = items.map((item) => {
    return formatItem(
      item,
      remove,
      update,
      increment,
      decrement,
      moveToWishlist,
      changeSize
    );
  });

  return { formattedItems };
};

const useItems = () => {
  const { cart } = useLegacySelection();
  const items = cart?.items;
  const { formattedItems } = useFormatItems(items);

  return { cartItems: formattedItems };
};

export default useItems;

export type CartItemWithHooks = ReturnType<typeof formatItem>;
export type CartItemMaybeHooked = CartItem | CartItemWithHooks;
